import React from 'react';
import { Neo } from '@singularsystems/neo-react';
import InvestorProfileVM from './InvestorProfileVM';
import { observer } from 'mobx-react';
import ETViewBase from '../ETViewBase';
import InvestmentSelectionComponent from '../Dashboard/Components/InvestmentSelectionComponent';
import StatusImage from '../../App/Components/StatusImage';
import { InfoPanelView } from '../InformationPanel/AccountInformationVM';
import ClickLink from '../../App/Components/ClickLink';
import { FicaStatus } from '../../Models/Enums/FicaStatus';
import InvestorTypeSectionConfig from '../../Models/Investors/InvestorTypeSectionConfig';
import InvestorFicaStatusLookup from '../../Models/Investors/InvestorFicaStatusLookup';

@observer
export default class InvestorProfileView extends ETViewBase<InvestorProfileVM> {

    constructor(props: unknown) {
        super("Investor Profile", InvestorProfileVM, props);
    }

    public render() {
        const viewModel = this.viewModel,
            investorService = viewModel.investorService,
            investorData = investorService.investorData!,
            ficaStatuses = investorData.ficaStatuses;

        const visibleConfigs = viewModel.sectionConfigs
            .filter(config => config.isVisible);

        return (
            <div>
                <InvestmentSelectionComponent hideInvestmentType />

                <div className="mt-4">
                    {ficaStatuses && visibleConfigs.length > 0 ? (
                        <Neo.GridLayout md={2} xl={3} verticalSpace="2rem">
                            {visibleConfigs.map(config => this.renderSection(config, ficaStatuses))}
                        </Neo.GridLayout>
                    ) : (
                        <p>No account information sections to display.</p>
                    )}
                </div>
            </div>
        );
    }

    private renderSection(config: InvestorTypeSectionConfig, ficaStatuses: InvestorFicaStatusLookup) {

        switch (config.sectionKey) {
            case "Identity":
                return this.renderIdentitySection(config, ficaStatuses);
            case "ProofOfResidence":
                return this.renderAddressSection(config, ficaStatuses);
            case "BankDetails":
                return this.renderBankDetailsSection(config, ficaStatuses);
            case "TaxDetails":
                return this.renderTaxDetailsSection(config, ficaStatuses);
            case "ThirdPartyBank":
                return this.renderThirdPartyBankDetailsSection(config, ficaStatuses);
            default:
                return null;
        }
    }

    private renderIdentitySection(config: InvestorTypeSectionConfig, ficaStatuses: InvestorFicaStatusLookup) {
        const investorService = this.viewModel.investorService;

        return (
            <div key={config.sectionKey} className="content-box">
                <div className="dashboard-status-box">
                    <div className="pe-2">
                        <span>{config.displayLabel}</span>
                        <ClickLink onClick={() => investorService.beginShowAccountInformationPanel(InfoPanelView.Identity)}>VIEW ID</ClickLink>
                        {(ficaStatuses.identityStatus === FicaStatus.Rejected &&
                            <small>{ficaStatuses.identityFailedReason}</small>)}
                    </div>
                    <div>
                        <StatusImage status={ficaStatuses.identityStatus} />
                    </div>
                </div>
            </div>
        );
    }

    private renderAddressSection(config: InvestorTypeSectionConfig, ficaStatuses: InvestorFicaStatusLookup) {
        const investorData = this.viewModel.investorService.investorData!;
        const investorService = this.viewModel.investorService;

        return (
            <div key={config.sectionKey} className="content-box">
                <div className="dashboard-status-box">
                    <div className="pe-2">
                        <span>{config.displayLabel}</span>
                        <ClickLink onClick={() => investorService.beginShowAccountInformationPanel(InfoPanelView.Address)}>VIEW PROOF</ClickLink>
                        {ficaStatuses.addressStatus === FicaStatus.Rejected &&
                            <div>
                                <small>{ficaStatuses.addressFailedReason}</small>
                                <div className="mt-3">
                                    <Neo.Button isOutline onClick={() => investorData.accountInformationVM.beginEditAddress(this.taskRunner)}>UPLOAD NEW</Neo.Button>
                                </div>
                            </div>}
                    </div>
                    <div>
                        <StatusImage status={ficaStatuses.addressStatus} />
                    </div>
                </div>
            </div>
        );
    }

    private renderBankDetailsSection(config: InvestorTypeSectionConfig, ficaStatuses: InvestorFicaStatusLookup) {
        const investorData = this.viewModel.investorService.investorData!;
        const investorService = this.viewModel.investorService;

        return (
            <div key={config.sectionKey} className="content-box">
                <div className="dashboard-status-box">
                    <div className="pe-2">
                        <span>{config.displayLabel}</span>
                        <ClickLink onClick={() => investorService.beginShowAccountInformationPanel(InfoPanelView.BankDetails)}>VIEW DETAILS</ClickLink>
                        {(ficaStatuses.bankDetailsStatus === null || ficaStatuses.bankDetailsStatus === FicaStatus.Rejected) &&
                            <div className="mt-2">
                                <Neo.Button isOutline onClick={() => investorData.accountInformationVM.beginEditBankDetails()}>UPLOAD NEW</Neo.Button>
                            </div>}
                    </div>
                    <div>
                        <StatusImage status={ficaStatuses.bankDetailsStatus} />
                    </div>
                </div>
            </div>
        );
    }

    private renderTaxDetailsSection(config: InvestorTypeSectionConfig, ficaStatuses: InvestorFicaStatusLookup) {
        const investorData = this.viewModel.investorService.investorData!;
        const investorService = this.viewModel.investorService;

        return (
            <div key={config.sectionKey} className="content-box">
                <div className="dashboard-status-box">
                    <div className="pe-2">
                        <span>{config.displayLabel}</span>
                        <ClickLink onClick={() => investorService.beginShowAccountInformationPanel(InfoPanelView.TaxDetails)}>VIEW DETAILS</ClickLink>
                        {ficaStatuses.taxStatus === FicaStatus.Rejected &&
                            <div>
                                <small>{ficaStatuses.taxFailedReason}</small>
                                <div className="mt-3">
                                    <Neo.Button isOutline onClick={() => investorData.accountInformationVM.beginEditTaxDetails()}>UPLOAD NEW</Neo.Button>
                                </div>
                            </div>}
                    </div>
                    <div>
                        <StatusImage status={ficaStatuses.taxStatus} />
                    </div>
                </div>
            </div>
        );
    }

    private renderThirdPartyBankDetailsSection(config: InvestorTypeSectionConfig, ficaStatuses: InvestorFicaStatusLookup) {
        const investorData = this.viewModel.investorService.investorData!;
        const investorService = this.viewModel.investorService;

        return (
            <div key={config.sectionKey} className="content-box">
                <div className="dashboard-status-box">
                    <div className="pe-2">
                        <span>{config.displayLabel}</span>
                        <ClickLink onClick={() => investorService.beginShowAccountInformationPanel(InfoPanelView.ThirdPartyBankDetails)}>VIEW DETAILS</ClickLink>
                        {(ficaStatuses.thirdPartyBankDetailsStatus === null) &&
                            <div className="mt-2">
                                <ClickLink onClick={() => investorData.accountInformationVM.beginEdit3rdPartyBankDetails()}><i className="fa fa-plus" /> Add 3rd Party Bank Details</ClickLink>
                            </div>}
                        {(ficaStatuses.thirdPartyBankDetailsStatus === FicaStatus.Rejected) &&
                            <div className="mt-2">
                                <Neo.Button isOutline onClick={() => investorData.accountInformationVM.beginEdit3rdPartyBankDetails()}>UPLOAD NEW</Neo.Button>
                            </div>}
                    </div>
                    <div>
                        <StatusImage status={ficaStatuses.thirdPartyBankDetailsStatus} />
                    </div>
                </div>
            </div>
        );
    }
}